// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import type { ProductStateResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n, formatEurFromCents } from "@/i18n"
import { InfoButton } from "@/components/ui/InfoButton"
import { glossaryTerm } from "@/lib/glossary"
import { fetchBudgetEconomics } from "@/lib/productState"

/**
 * Insights (Budget Model v2, ADR-052 §11.3): the CURRENT forecast weekly
 * average (labelled estimate, from this week's plan) and "Actual weekly
 * average — Not tracked yet" with the future-capture note. No trend chart:
 * there is no revision-history endpoint yet, so a single honest point is
 * all that can be shown. Everything comes from GET /v1/product-state; a
 * failed fetch renders nothing rather than a guess.
 */
export function BudgetForecastBlock() {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [state, setState] = useState<ProductStateResponse["budgetEconomics"] | null>(null)

  useEffect(() => {
    let cancelled = false
    void fetchBudgetEconomics(api).then(res => {
      if (!cancelled) setState(res)
    })
    return () => {
      cancelled = true
    }
  }, [api])

  if (!state) return null
  const forecast = state.forecastWeeklyAverage
  const actualName = glossaryTerm("actual-weekly-average", locale).name

  return (
    <section
      aria-label={glossaryTerm("forecast-weekly-average", locale).name}
      className="flex flex-col gap-4 rounded-bb-md border border-line bg-paper p-5"
      data-testid="insights-budget-block"
    >
      <div>
        <p className="flex items-center gap-1 text-sm font-semibold text-ink">
          {t("views.budget.insightsForecastTitle")}
          <InfoButton termId="forecast-weekly-average" />
        </p>
        <p className="mt-1 text-lg font-bold text-ink">
          {forecast.available && forecast.cents !== null
            ? `${formatEurFromCents(forecast.cents, locale)} ${t("views.budget.perWeek")}`
            : forecast.unavailableReason === "no_current_plan"
              ? t("views.budget.noPlanYet")
              : glossaryTerm("items-without-reliable-price", locale).name}
        </p>
        <p className="text-xs text-ink-2">{t("views.budget.insightsForecastNote")}</p>
      </div>
      <div>
        <p className="flex items-center gap-1 text-sm font-semibold text-ink">
          {actualName}
          <InfoButton termId="actual-weekly-average" />
        </p>
        {/* Owner D-4: literally not tracked — never derived from estimates. */}
        <p className="mt-1 text-lg font-bold text-ink">{t("views.budget.notTrackedYet")}</p>
        <p className="text-xs text-ink-2">{t("views.budget.insightsActualCta")}</p>
      </div>
    </section>
  )
}
